import React, { useMemo } from "react";

import MissionDetails from "../../components/editors/MissionDetails";
import { MockedAuthManager } from "../../contexts/AuthManager";
import { MockedProvider } from "@apollo/client/testing";
import {
    buildActivityType,
    buildClientInfo,
    buildContract,
    buildMission,
    buildSalaryForecast,
    buildUser,
    buildWorkerInfo,
} from "../factories";
import { ProfileType } from "../../__generated__/graphql";
import type { Meta, StoryObj } from "@storybook/react";
import { ScrollView } from "react-native";
import { GET_SALARY_FORECAST } from "../../graphql/salaries";
import { MockedProfileSwitcherManager } from "../../contexts/ProfileSwitcherManager";

function MissionDetailsStoryComponent({ profileType }: { profileType: ProfileType }) {
    const user = useMemo(buildUser, []);
    const mission = useMemo(
        () =>
            buildMission({
                id: "uuid-uuid-uuid-uuid",
                activityType: buildActivityType({
                    title: "Mangeur de pomme | Mangeuse de pomme",
                }),
                currentContract: buildContract({
                    jobTitle: "Senior apple eater",
                    startDate: "2012-01-01",
                    endDate: "2013-04-30",
                }),
                workerInfo: buildWorkerInfo({
                    name: "Pedro Lane",
                }),
                clientInfo: buildClientInfo({
                    name: "Mercedes",
                }),
            }),
        [],
    );
    const mocks = useMemo(
        () => [
            {
                request: {
                    query: GET_SALARY_FORECAST,
                },
                variableMatcher: () => true,
                result: () => ({
                    data: {
                        getSalaryForecast: [buildSalaryForecast()],
                    },
                }),
            },
        ],
        [],
    );
    return (
        <MockedAuthManager user={user}>
            <MockedProfileSwitcherManager profileType={profileType}>
                <MockedProvider mocks={mocks}>
                    <MissionDetails mission={mission} profileType={profileType} />
                </MockedProvider>
            </MockedProfileSwitcherManager>
        </MockedAuthManager>
    );
}

const meta = {
    title: "Views/Mission Details",
    component: MissionDetailsStoryComponent,
    argTypes: {
        profileType: {
            options: [ProfileType.Worker, ProfileType.Client],
            control: { type: "radio" },
        },
    },
    decorators: [
        (StoryFn) => (
            <ScrollView>
                <StoryFn />
            </ScrollView>
        ),
    ],
} satisfies Meta<typeof MissionDetailsStoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Worker: Story = {
    args: {
        profileType: ProfileType.Worker,
    },
};

export const Client: Story = {
    args: {
        profileType: ProfileType.Client,
    },
};
